import { useCallback, useState } from "react";
import { FeatureFlagKey } from "../../../../common/enums";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useFeatureFlag } from "../../../../common/infrastructure/hooks/useFeatureFlag";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { IReviewFormModel } from "../../../reviews/models/reviewFormModel";
import { IMyStudioReviewModel } from "../../../reviews/models/reviewModels";
import { toReviewPayload } from "../../../reviews/utils/reviewUtility";

/**
 * The client's own review of the selected studio, behind "Остави ревю" /
 * "Редактирай ревюто" in "Моите резервации". One review per studio. While the reviews
 * flag is off nobody can review, and nothing is asked.
 */
export const useMyStudioReview = (studioId?: number) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isReviewsEnabled = useFeatureFlag(FeatureFlagKey.EnableReviewRequests, false);

  const { data } = useApiQuery<IMyStudioReviewModel>({
    queryKey: [endpointsMetadata.portalReviews.getMine, studioId],
    endpoint: endpointsMetadata.portalReviews.getMine,
    enabled: isReviewsEnabled && !!studioId,
    globalIsLoading: false,
    retry: false,
  });

  const { mutate, isLoading: isSaving } = useApiMutation();

  const review = data?.review ?? null;

  const openDialog = useCallback(() => setIsDialogOpen(true), []);
  const closeDialog = useCallback(() => setIsDialogOpen(false), []);

  const save = useCallback(
    (values: IReviewFormModel) => {
      mutate(
        {
          endpoint: endpointsMetadata.portalReviews.saveMine,
          method: "post",
          payload: toReviewPayload(values),
          invalidateKeys: [
            [endpointsMetadata.portalReviews.getMine, studioId],
            [endpointsMetadata.publicReviews.getSummary, studioId],
          ],
          successMessage: "Благодарим за ревюто!",
        },
        { onSuccess: () => setIsDialogOpen(false) },
      );
    },
    [mutate, studioId],
  );

  return {
    canReview: isReviewsEnabled && (data?.canReview ?? false),
    review,
    hasReview: !!review,
    initialValues: review
      ? { rating: review.rating, comment: review.comment ?? "" }
      : undefined,
    isDialogOpen,
    isSaving,
    openDialog,
    closeDialog,
    save,
  };
};
